/**
 * AmbNestedTabs - A class to handle nested tab functionality with URL state management
 * Supports both main tabs and sub-tabs, maintaining state through URL parameters
 */
class AmbNestedTabs {
    constructor() {
        this.init();
    }

    /**
     * Updates the URL to reflect current tab state
     * Handles both main tabs and sub-tabs, ensuring proper hierarchy
     * @param {HTMLElement} button - The clicked tab button
     */
    updateURL(button) {
        // Check if this is a sub-tab by examining the parent structure
        const isSubTab = button.closest('.amb-tabs').parentElement.classList.contains('amb-tab-content');
        const params = new URLSearchParams(window.location.search);

        if (isSubTab) {
            // For sub-tabs, we need to maintain the parent tab's state
            const parentTabContent = button.closest('.amb-tab-content');
            const parentTab = document.querySelector(`.amb-tabs__item[data-tab="${parentTabContent.id}"]`);
            if (parentTab) {
                params.set('tab', parentTab.getAttribute('data-tab-link'));
            }
            params.set('sub-tab', button.getAttribute('data-tab-link'));
        } else {
            // For main tabs, reset sub-tab state
            params.set('tab', button.getAttribute('data-tab-link'));
            params.delete('sub-tab');
        }

        // Update URL without page reload
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }

    /**
     * Deactivates all tabs in a specific tab group
     * Handles both main tabs and their associated sub-tabs
     * @param {HTMLElement} tabContainer - The container element for the tab group
     */
    removeActiveClasses(tabContainer) {
        // First, deactivate all tab buttons in the current level
        const tabButtons = tabContainer.querySelector('.amb-tabs__nav').querySelectorAll('.amb-tabs__item');
        tabButtons.forEach(btn => btn.classList.remove('active'));

        // Then, handle the tab contents and their potential sub-tabs
        const tabContents = Array.from(tabContainer.children);
        const contentContainer = tabContents.find(child => child.classList.contains('amb-tabs__content'));

        if (contentContainer) {
            // Deactivate all main tab contents and their sub-components
            contentContainer.querySelectorAll(':scope > .amb-tab-content').forEach(content => {
                content.classList.remove('active');
                // Recursively deactivate any nested tab elements
                const subElements = content.querySelectorAll('.amb-tabs__item, .amb-tab-content');
                subElements.forEach(element => element.classList.remove('active'));
            });
        }
    }

    /**
     * Activates a specific tab and its associated content
     * @param {HTMLElement} button - The tab button to activate
     * @param {HTMLElement} content - The associated content panel
     */
    setActiveTab(button, content) {
        if (!button) return;
        button.classList.add('active');
        if (content) {
            content.classList.add('active');
        }
    }

    /**
     * Handles the tab click event and manages tab state
     * Coordinates between main tabs and sub-tabs, ensuring proper hierarchy
     * @param {HTMLElement} button - The clicked tab button
     */
    handleTabClick(button) {
        // Get necessary elements and determine tab type
        const tabId = button.getAttribute('data-tab');
        const tabContent = document.getElementById(tabId);
        const tabContainer = button.closest('.amb-tabs');
        const isMainTab = !tabContainer.parentElement.classList.contains('amb-tab-content');

        // Clear current active states
        this.removeActiveClasses(tabContainer);

        // Set new active states
        this.setActiveTab(button, tabContent);

        // Handle main tab's default sub-tab activation
        if (isMainTab && tabContent) {
            const firstSubTab = tabContent.querySelector('.amb-tabs__nav .amb-tabs__item');
            if (firstSubTab) {
                const subTabId = firstSubTab.getAttribute('data-tab');
                const subTabContent = document.getElementById(subTabId);
                this.setActiveTab(firstSubTab, subTabContent);
            }
        }

        // Sync URL with current tab state
        this.updateURL(button);
    }

    /**
     * Activates tabs based on URL parameters
     * Handles both main tabs and sub-tabs, with fallback to default selections
     */
    activateTabFromURL() {
        const params = new URLSearchParams(window.location.search);
        const tabId = params.get('tab');
        const subTabId = params.get('sub-tab');

        // Reset all tabs to inactive state
        document.querySelectorAll('.amb-tabs').forEach(container => {
            this.removeActiveClasses(container);
        });

        // Find and activate main tab
        const mainButton = tabId
            ? document.querySelector(`.amb-tabs__item[data-tab-link="${tabId}"]`)
            : document.querySelector('.amb-tabs > .amb-tabs__nav .amb-tabs__item'); // Default to first tab

        if (mainButton) {
            const mainTabId = mainButton.getAttribute('data-tab');
            const mainTabContent = document.getElementById(mainTabId);
            this.setActiveTab(mainButton, mainTabContent);

            // Find and activate sub-tab
            const subButton = subTabId
                ? document.querySelector(`.amb-tabs__item[data-tab-link="${subTabId}"]`)
                : mainTabContent?.querySelector('.amb-tabs__nav .amb-tabs__item'); // Default to first sub-tab

            if (subButton) {
                const subTabContentId = subButton.getAttribute('data-tab');
                this.setActiveTab(subButton, document.getElementById(subTabContentId));
            }
        }
    }

    /**
     * Initializes the nested tabs functionality
     * Sets up event listeners and initializes tab state from URL
     */
    init() {
        // Setup tab containers and event listeners
        document.querySelectorAll('.amb-tabs').forEach(container => {
            // Reset any pre-existing active states
            this.removeActiveClasses(container);

            // Attach click handlers to all tab buttons
            container.querySelectorAll('.amb-tabs__item').forEach(button => {
                button.addEventListener('click', () => this.handleTabClick(button));
            });
        });

        // Initialize tabs based on URL parameters
        this.activateTabFromURL();
    }
}

// Initialize the nested tabs functionality when DOM is ready
document.addEventListener('DOMContentLoaded', () => new AmbNestedTabs());